import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { toast } from "sonner";
import { ArrowUpRight, Clock, CheckCircle2, PlayCircle, BookOpen } from "lucide-react";

function fmt(date) {
  try {
    const d = new Date(date);
    return d.toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return date;
  }
}

function ScoreBadge({ score }) {
  const color =
    score >= 80 ? "text-emerald-700 bg-emerald-50 border-emerald-200" :
    score >= 60 ? "text-orange-700 bg-orange-50 border-orange-200" :
                  "text-red-700 bg-red-50 border-red-200";
  return (
    <div className={`flex flex-col items-center border rounded-xl px-4 py-2.5 ${color}`}>
      <span className="font-display text-2xl font-bold leading-none">{score}</span>
      <span className="text-[9px] uppercase tracking-widest mt-0.5 font-semibold opacity-70">score</span>
    </div>
  );
}

export default function History() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/sessions")
      .then((res) => setItems(res.data))
      .catch((err) => toast.error(formatApiError(err)))
      .finally(() => setLoading(false));
  }, []);

  const completed = items.filter((i) => i.status === "completed");
  const inProgress = items.filter((i) => i.status !== "completed");

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Page header */}
      <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
        <div className="absolute -top-16 -right-16 w-80 h-80 rounded-full bg-blue-600/10 blur-3xl" aria-hidden />
        <div className="absolute bottom-0 left-8 w-64 h-64 rounded-full bg-orange-500/10 blur-3xl" aria-hidden />
        <div className="relative max-w-5xl mx-auto px-6 sm:px-10 py-12 md:py-14">
          <div className="text-xs font-semibold uppercase tracking-widest text-blue-400 mb-2">Your history</div>
          <h1 className="font-display text-3xl md:text-4xl text-white tracking-tight font-bold">Past role-plays.</h1>
          <p className="mt-2 text-slate-400">Revisit a session to read the transcript and your coach review.</p>

          {/* Mini stats */}
          {items.length > 0 && (
            <div className="mt-6 flex gap-6 text-sm">
              <div className="flex items-center gap-2 text-slate-300">
                <CheckCircle2 className="w-4 h-4 text-emerald-400" strokeWidth={1.75} />
                <span><b className="text-white">{completed.length}</b> completed</span>
              </div>
              <div className="flex items-center gap-2 text-slate-300">
                <PlayCircle className="w-4 h-4 text-orange-400" strokeWidth={1.75} />
                <span><b className="text-white">{inProgress.length}</b> in progress</span>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-6 sm:px-10 py-10">

        {loading ? (
          <div className="text-slate-500 text-sm text-center py-20">Loading…</div>
        ) : items.length === 0 ? (
          <div className="mt-4 bg-slate-50 border border-dashed border-slate-200 rounded-2xl p-12 text-center">
            <BookOpen className="w-10 h-10 text-slate-300 mx-auto mb-4" strokeWidth={1.5} />
            <div className="font-display text-xl font-bold text-slate-900">No sessions yet</div>
            <p className="mt-2 text-slate-500 max-w-sm mx-auto">
              Pick a scenario and have your first practice conversation.
            </p>
            <Link
              to="/scenarios"
              className="inline-flex items-center mt-6 px-6 py-2.5 rounded-xl bg-[#EA580C] hover:bg-[#C2410C] text-white text-sm font-semibold shadow-sm shadow-orange-200 transition-colors"
            >
              Browse scenarios <ArrowUpRight className="w-4 h-4 ml-1.5" strokeWidth={2} />
            </Link>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="history-list">
            {items.map((it) => {
              const target = it.status === "completed" ? `/session/${it.id}/review` : `/session/${it.id}`;
              const isComplete = it.status === "completed";
              return (
                <li key={it.id}>
                  <Link
                    to={target}
                    data-testid={`history-item-${it.id}`}
                    className="flex items-center gap-4 bg-white border border-slate-200 rounded-2xl p-5 hover:border-orange-200 hover:shadow-lg transition-all duration-300 group"
                  >
                    {/* Status icon */}
                    <div className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${
                      isComplete ? "bg-emerald-50" : "bg-orange-50"
                    }`}>
                      {isComplete
                        ? <CheckCircle2 className="w-5 h-5 text-emerald-500" strokeWidth={1.75} />
                        : <PlayCircle className="w-5 h-5 text-orange-500" strokeWidth={1.75} />
                      }
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="text-[10px] font-semibold tracking-widest uppercase text-blue-600 mb-0.5">
                        {it.scenario_category}
                      </div>
                      <div className="font-display text-base font-bold text-slate-900 truncate group-hover:text-[#EA580C] transition-colors">
                        {it.scenario_title}
                      </div>
                      <div className="flex items-center gap-1.5 text-xs text-slate-400 mt-0.5">
                        <Clock className="w-3 h-3" strokeWidth={1.75} />
                        {fmt(it.started_at)}
                      </div>
                    </div>

                    {/* Score or status */}
                    <div className="shrink-0">
                      {isComplete && it.overall_score != null ? (
                        <ScoreBadge score={it.overall_score} />
                      ) : (
                        <span className="text-xs px-3 py-1.5 rounded-full bg-orange-50 text-orange-700 border border-orange-200 font-medium">
                          In progress
                        </span>
                      )}
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-slate-300 group-hover:text-[#EA580C] transition-colors shrink-0" strokeWidth={1.75} />
                  </Link>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
